import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
const REFRESH_INTERVAL_MS = 30 * 1000;
const FRESH_SECONDS = 60;
const TOP_N = 5;
const OCCUPANCY_LABELS = {
  EMPTY: "Empty",
  MANY_SEATS_AVAILABLE: "Seats available",
  FEW_SEATS_AVAILABLE: "Few seats available",
  STANDING_ROOM_ONLY: "Standing room only",
  CRUSHED_STANDING_ROOM_ONLY: "Very limited standing room",
  FULL: "Full",
  NOT_ACCEPTING_PASSENGERS: "Not accepting passengers",
  NOT_BOARDABLE: "Not boardable",
  NO_DATA_AVAILABLE: "No data available",
  UNKNOWN: "Not reported"
};
async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }
  return response.json();
}
function formatPercentage(part, whole) {
  if (!whole) {
    return "0.0";
  }
  return (part / whole * 100).toFixed(1);
}
function formatLastUpdated(lastUpdatedAt) {
  if (!lastUpdatedAt) {
    return "No feed timestamp";
  }
  return new Date(lastUpdatedAt).toLocaleString("en-NZ", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Pacific/Auckland"
  });
}
function formatDelay(seconds) {
  const absoluteSeconds = Math.abs(seconds);
  const minutes = Math.floor(absoluteSeconds / 60);
  const remainder = absoluteSeconds % 60;
  const sign = seconds > 0 ? "+" : seconds < 0 ? "-" : "";
  return `${sign}${minutes}m ${remainder}s`;
}
function getDelaySnapshot(samples, nowSec) {
  const live = (samples ?? []).filter(sample => {
    const timestamp = Number(sample.timestamp);
    return Number.isFinite(timestamp) && timestamp > 0 && timestamp <= nowSec && nowSec - timestamp < FRESH_SECONDS;
  });
  const withDelay = live.map(({
    delay,
    tripId,
    routeId
  }) => {
    return {
      delay: typeof delay === "number" && Number.isFinite(delay) ? delay : null,
      tripId: tripId ?? null,
      routeId: routeId ?? null
    };
  }).filter(item => item.delay !== null);
  const count = withDelay.length;
  const delays = withDelay.map(item => item.delay).sort((a, b) => a - b);
  return {
    count,
    lateCount: withDelay.filter(item => item.delay > 0).length,
    onTimeCount: withDelay.filter(item => item.delay === 0).length,
    earlyCount: withDelay.filter(item => item.delay < 0).length,
    meanDelay: count > 0 ? Math.round(delays.reduce((total, delay) => total + delay, 0) / count) : 0,
    medianDelay: count > 0 ? delays[Math.floor(count / 2)] : 0,
    topLate: withDelay.filter(item => item.delay > 0).sort((a, b) => b.delay - a.delay).slice(0, TOP_N),
    topEarly: withDelay.filter(item => item.delay < 0).sort((a, b) => a.delay - b.delay).slice(0, TOP_N)
  };
}
function SummaryCard(t0) {
  const $ = _c(10);
  const {
    title,
    value,
    subtitle
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="text-sm text-zinc-500 dark:text-zinc-400">{title}</div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <div className="mt-1 text-3xl font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">{value}</div>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== subtitle) {
    t3 = subtitle ? <div className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{subtitle}</div> : null;
    $[4] = subtitle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="rounded-2xl border border-zinc-200 bg-white/85 p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/70">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function OccupancyBadge(t0) {
  const $ = _c(7);
  const {
    status,
    count
  } = t0;
  const t1 = OCCUPANCY_LABELS[status] ?? status;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="font-medium">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== count) {
    t3 = <span className="tabular-nums text-zinc-500 dark:text-zinc-400">{count}</span>;
    $[2] = count;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-sm dark:border-zinc-800 dark:bg-zinc-950">{t2}{" "}{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function TopDelayList(t0) {
  const $ = _c(7);
  const {
    title,
    items
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <div className="border-b border-zinc-200 p-4 dark:border-zinc-800"><h2 className="text-lg font-semibold">{title}</h2></div>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== items) {
    t2 = items.length ? <ul className="divide-y divide-zinc-100 dark:divide-zinc-900">{items.map(_temp)}</ul> : <p className="p-4 text-sm text-zinc-500 dark:text-zinc-400">Nothing to show.</p>;
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white/85 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/70">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp(item, index) {
  return <li key={`${item.tripId ?? "trip"}-${index}`} className="p-4">{item.tripId ? <a className="flex items-center justify-between gap-3" href={`/trip/${encodeURIComponent(item.tripId)}`}><span className="font-medium tabular-nums">{formatDelay(item.delay)}</span>{item.routeId ? <span className="rounded-full border border-zinc-200 px-2 py-0.5 text-xs text-zinc-600 dark:border-zinc-800 dark:text-zinc-300">Route {item.routeId}</span> : null}</a> : <span className="font-medium tabular-nums">{formatDelay(item.delay)}</span>}</li>;
}
export default function OperationsDashboard({
  regionName = "Catchy"
}) {
  const [stats, setStats] = useState(null);
  const [delaySamples, setDelaySamples] = useState(null);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    let cancelled = false;
    let refreshTimeout = null;
    let requestInFlight = false;
    const canRefresh = () => document.visibilityState !== "hidden" && navigator.onLine !== false;
    const scheduleRefresh = () => {
      if (cancelled) return;
      if (refreshTimeout !== null) {
        window.clearTimeout(refreshTimeout);
      }
      refreshTimeout = window.setTimeout(() => {
        refreshTimeout = null;
        void load();
      }, REFRESH_INTERVAL_MS);
    };
    async function load() {
      if (cancelled || requestInFlight) return;
      if (!canRefresh()) {
        scheduleRefresh();
        return;
      }
      requestInFlight = true;
      setIsRefreshing(true);
      try {
        const snapshot = await fetchJson("/api/operations/snapshot");
        if (!cancelled) {
          setStats(snapshot.stats);
          setDelaySamples(snapshot.delaySamples);
          setError(null);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError);
        }
      } finally {
        requestInFlight = false;
        if (!cancelled) {
          setIsRefreshing(false);
          scheduleRefresh();
        }
      }
    }
    const resumeRefresh = () => {
      if (!canRefresh() || requestInFlight) return;
      if (refreshTimeout !== null) {
        window.clearTimeout(refreshTimeout);
        refreshTimeout = null;
      }
      void load();
    };
    void load();
    document.addEventListener("visibilitychange", resumeRefresh);
    window.addEventListener("online", resumeRefresh);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", resumeRefresh);
      window.removeEventListener("online", resumeRefresh);
      if (refreshTimeout !== null) {
        window.clearTimeout(refreshTimeout);
      }
    };
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setNowSec(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const delaySnapshot = getDelaySnapshot(delaySamples, nowSec);
  const loading = !stats && !delaySamples && !error;
  return <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(251,146,60,0.20),_transparent_28rem),linear-gradient(180deg,_#fafaf9,_#eef2ff)] px-5 py-6 text-zinc-950 dark:bg-[radial-gradient(circle_at_top_left,_rgba(251,146,60,0.18),_transparent_28rem),linear-gradient(180deg,_#09090b,_#18181b)] dark:text-zinc-50">
      <div className="mx-auto max-w-6xl">
        <a className="text-sm font-medium text-zinc-500 dark:text-zinc-400" href="/">
          Catchy
        </a>
        <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600 dark:text-orange-300">
              Operations dashboard
            </p>
            <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">
              {regionName} live network
            </h1>
            <p className="mt-3 max-w-2xl text-zinc-600 dark:text-zinc-300">
              Live fleet mix, occupancy and the current early/late snapshot from
              the shared realtime feeds.
            </p>
          </div>
          <div className="rounded-full border border-zinc-200 bg-white/80 px-4 py-2 text-sm text-zinc-600 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/70 dark:text-zinc-300">
            {isRefreshing ? "Refreshing..." : "Refreshes every 30 seconds while visible"}
          </div>
        </div>

        {error ? <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300">
            Couldn&apos;t load the operations dashboard.
          </div> : null}

        {loading ? <p className="mt-6 text-zinc-500 dark:text-zinc-400">Loading...</p> : null}

        {stats ? <>
            <div className="mt-6 text-sm text-zinc-500 dark:text-zinc-400">
              Vehicle feed updated {formatLastUpdated(stats.lastUpdatedAt)}
            </div>

            <section className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <SummaryCard title="Live vehicles" value={stats.liveVehicleCount} />
              <SummaryCard title="Classified" value={stats.classifiedVehicleCount} subtitle={`${formatPercentage(stats.classifiedVehicleCount, stats.liveVehicleCount)}% of live vehicles`} />
              <SummaryCard title="Electric" value={stats.electricCount} subtitle={`${formatPercentage(stats.electricCount, stats.classifiedVehicleCount)}% of classified`} />
              <SummaryCard title="Not electric" value={stats.nonElectricCount} subtitle={`${formatPercentage(stats.nonElectricCount, stats.classifiedVehicleCount)}% of classified`} />
              <SummaryCard title="Unclassified" value={stats.unknownVehicleCount} subtitle="No matching fleet metadata" />
              {stats.ignoredUnroutedVehicleCount ? <SummaryCard title="Ignored feed items" value={stats.ignoredUnroutedVehicleCount} subtitle="No route attached" /> : null}
            </section>

            {stats.vehicleTypeBreakdown?.length ? <section className="mt-8">
                <h2 className="mb-3 text-xl font-semibold">By vehicle type</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {stats.vehicleTypeBreakdown.map(bucket => <SummaryCard key={bucket.key} title={bucket.label} value={bucket.count} subtitle={`${formatPercentage(bucket.count, stats.liveVehicleCount)}% of live vehicles`} />)}
                </div>
              </section> : null}

            {stats.occupancyByVehicleType?.length ? <section className="mt-8">
                <h2 className="mb-3 text-xl font-semibold">Occupancy</h2>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {stats.occupancyByVehicleType.map(bucket_0 => <div key={bucket_0.key} className="rounded-2xl border border-zinc-200 bg-white/85 p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950/70">
                      <div className="mb-3 flex items-baseline justify-between gap-3">
                        <h3 className="text-lg font-semibold">
                          {bucket_0.label}
                        </h3>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">
                          {bucket_0.count} live vehicle
                          {bucket_0.count === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {bucket_0.occupancy.map(occupancy => <OccupancyBadge key={`${bucket_0.key}-${occupancy.status}`} status={occupancy.status} count={occupancy.count} />)}
                      </div>
                    </div>)}
                </div>
              </section> : null}
          </> : null}

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-semibold">On-time snapshot</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard title="Live trip updates" value={delaySnapshot.count} />
            <SummaryCard title="Late" value={delaySnapshot.lateCount} subtitle={`${formatPercentage(delaySnapshot.lateCount, delaySnapshot.count)}%`} />
            <SummaryCard title="On time" value={delaySnapshot.onTimeCount} subtitle={`${formatPercentage(delaySnapshot.onTimeCount, delaySnapshot.count)}%`} />
            <SummaryCard title="Early" value={delaySnapshot.earlyCount} subtitle={`${formatPercentage(delaySnapshot.earlyCount, delaySnapshot.count)}%`} />
            <SummaryCard title="Median delay" value={formatDelay(delaySnapshot.medianDelay)} />
            <SummaryCard title="Mean delay" value={formatDelay(delaySnapshot.meanDelay)} />
          </div>

          {delaySnapshot.count === 0 ? <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
              No live trip updates in the last {FRESH_SECONDS} seconds.
            </p> : <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <TopDelayList title={`Top ${TOP_N} most delayed`} items={delaySnapshot.topLate} />
              <TopDelayList title={`Top ${TOP_N} earliest`} items={delaySnapshot.topEarly} />
            </div>}
        </section>
      </div>
    </main>;
}